import { Link, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import Seo from "@/components/feature/Seo";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, getArticleBySlug, hotArticles } from "@/mocks/articles";
import type { ArticleMeta } from "@/mocks/articles";

function renderBlock(block: ArticleMeta["body"][number], key: number) {
  switch (block.type) {
    case "h2":
      return (
        <h2
          key={key}
          id={`section-${key}`}
          className="mt-10 scroll-mt-28 font-heading text-xl font-bold text-foreground-950 md:text-2xl"
        >
          {block.text}
        </h2>
      );
    case "quote":
      return (
        <blockquote
          key={key}
          className="my-7 rounded-r-xl border-l-4 border-accent-500 bg-accent-50/60 px-5 py-4"
        >
          <p className="font-heading text-lg font-medium leading-8 text-foreground-800">
            {block.text}
          </p>
          {block.cite && <cite className="mt-2 block text-sm not-italic text-foreground-500">—— {block.cite}</cite>}
        </blockquote>
      );
    case "list":
      return (
        <ul key={key} className="my-6 space-y-3">
          {block.items.map((it) => (
            <li key={it} className="flex gap-3 text-foreground-800">
              <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-800">
                <i className="ri-check-line text-xs" />
              </span>
              <span className="text-[17px] leading-8">{it}</span>
            </li>
          ))}
        </ul>
      );
    default:
      return (
        <p key={key} className="my-5 text-[17px] leading-8 text-foreground-800">
          {block.text}
        </p>
      );
  }
}

export default function ArticlePage() {
  const { slug = "" } = useParams();
  const article = getArticleBySlug(slug);

  if (!article) {
    return (
      <PageLayout dark={false}>
        <Seo title="内容不存在 - 看料" />
        <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 pt-20 text-center">
          <i className="ri-file-search-line text-6xl text-foreground-300" />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">这篇好料不存在或已被撤下</h1>
          <p className="mt-2 text-sm text-foreground-600">去看看今日精选里的其他内容吧。</p>
          <Link
            to="/"
            className="mt-6 inline-flex h-11 items-center gap-2 rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-home-line" /> 返回首页
          </Link>
        </div>
      </PageLayout>
    );
  }

  const related = [
    ...articles.filter((a) => a.category === article.category && a.slug !== article.slug),
    ...hotArticles.filter((a) => a.slug !== article.slug && a.category !== article.category),
  ].slice(0, 3);

  const headings = article.body
    .map((b, i) => (b.type === "h2" ? { id: `section-${i}`, text: b.text } : null))
    .filter(Boolean) as { id: string; text: string }[];

  return (
    <PageLayout dark={false}>
      <Seo
        title={`${article.title} - 看料 ${article.categoryName}精选`}
        description={article.excerpt}
        keywords={`看料,${article.categoryName},${article.title},${article.tag.join(",")}`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: article.title,
            description: article.excerpt,
            image: article.cover,
            datePublished: `2026-${article.date}T10:00:00+08:00`,
            dateModified: `2026-${article.date}T10:00:00+08:00`,
            author: { "@type": "Person", name: article.author, jobTitle: article.authorRole },
            publisher: { "@type": "Organization", name: "看料" },
            mainEntityOfPage: { "@type": "WebPage", "@id": `https://01x0wyo.cn/article/${article.slug}` },
            articleSection: article.categoryName,
            keywords: article.tag.join(","),
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://01x0wyo.cn/" },
              { "@type": "ListItem", position: 2, name: article.categoryName, item: `https://01x0wyo.cn/category/${article.category}` },
              { "@type": "ListItem", position: 3, name: article.title, item: `https://01x0wyo.cn/article/${article.slug}` },
            ],
          },
        ]}
      />

      {/* 文章头 */}
      <header className="bg-background-50 pt-24 md:pt-28">
        <div className="mx-auto max-w-4xl px-4 md:px-6">
          <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to={`/category/${article.category}`} className="transition-colors hover:text-primary-700">
              {article.categoryName}
            </Link>
            <i className="ri-arrow-right-s-line" />
            <span className="truncate text-foreground-800 max-w-[180px]">{article.title}</span>
          </nav>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <Link
              to={`/category/${article.category}`}
              className="rounded-md bg-primary-100 px-2.5 py-1 text-xs font-semibold text-primary-800 transition-colors hover:bg-primary-500 hover:text-background-50 whitespace-nowrap"
            >
              {article.categoryName}
            </Link>
            {article.tag.slice(0, 2).map((t) => (
              <span key={t} className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
                {t}
              </span>
            ))}
          </div>

          <h1 className="mt-5 font-heading text-3xl font-black leading-tight text-foreground-950 md:text-5xl md:leading-[1.2]">
            {article.title}
          </h1>

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-3 border-y border-background-200/70 py-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-500 text-lg font-bold text-background-50">
                {article.author.charAt(0)}
              </span>
              <div className="leading-tight">
                <p className="text-sm font-semibold text-foreground-900">{article.author}</p>
                <p className="text-xs text-foreground-500">{article.authorRole}</p>
              </div>
            </div>
            <span className="flex items-center gap-1.5 text-sm text-foreground-500">
              <i className="ri-calendar-line" /> {article.date}
            </span>
            <span className="flex items-center gap-1.5 text-sm text-foreground-500">
              <i className="ri-time-line" /> 约 {article.readTime}
            </span>
          </div>
        </div>
      </header>

      {/* 正文 + 侧栏 */}
      <div className="mx-auto max-w-7xl px-4 md:px-6 py-8 md:py-12">
        <div className="grid gap-10 lg:grid-cols-12">
          <article className="lg:col-span-8">
            <div className="overflow-hidden rounded-2xl border border-background-200/70">
              <img src={article.cover} alt={article.coverAlt} className="w-full object-cover" />
            </div>
            <p className="mt-8 font-heading text-lg font-medium leading-9 text-foreground-700 md:text-xl">
              {article.excerpt}
            </p>

            <div className="mt-4 border-t border-background-200/60 pt-2">
              {article.body.map((b, i) => renderBlock(b, i))}
            </div>

            {/* 结尾标签 */}
            <div className="mt-10 flex flex-wrap items-center gap-2 border-t border-background-200/70 pt-6">
              <span className="text-xs font-semibold text-foreground-500">本文标签：</span>
              {article.tag.map((t) => (
                <Link
                  key={t}
                  to={`/category/${article.category}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-background-100 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:bg-primary-100 hover:text-primary-800 whitespace-nowrap"
                >
                  <i className="ri-price-tag-3-line text-primary-500" /> {t}
                </Link>
              ))}
            </div>

            {/* 分享 */}
            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-background-100 px-5 py-4">
              <span className="text-sm font-semibold text-foreground-800">觉得不错？分享给朋友</span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(window.location.href)}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md bg-background-50 px-3 text-xs font-medium text-foreground-700 transition-colors hover:bg-primary-500 hover:text-background-50 whitespace-nowrap"
                >
                  <i className="ri-link-m" /> 复制链接
                </button>
                <a
                  href={`mailto:?subject=${encodeURIComponent(article.title)}&body=${encodeURIComponent(article.title + " - " + window.location.href)}`}
                  className="inline-flex h-9 items-center gap-1.5 rounded-md bg-background-50 px-3 text-xs font-medium text-foreground-700 transition-colors hover:bg-secondary-500 hover:text-background-50 whitespace-nowrap"
                >
                  <i className="ri-mail-send-line" /> 邮件分享
                </a>
              </div>
            </div>
          </article>

          {/* 侧栏 */}
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24 space-y-5">
              {headings.length > 0 && (
                <nav className="rounded-xl border border-background-200/80 bg-background-50 p-5" aria-label="文章目录">
                  <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                    <i className="ri-list-unordered text-primary-500" /> 本文目录
                  </h2>
                  <ul className="mt-3 space-y-2 border-l border-background-200 pl-3">
                    {headings.map((h) => (
                      <li key={h.id}>
                        <a
                          href={`#${h.id}`}
                          className="block text-sm leading-6 text-foreground-600 transition-colors hover:text-primary-700"
                        >
                          {h.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              )}

              <div className="rounded-xl border border-background-200/80 bg-accent-50/50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                  <i className="ri-quill-pen-line text-accent-600" /> 编辑备注
                </h2>
                <p className="mt-3 text-sm leading-7 text-foreground-700">
                  「{article.title}」由{article.author}编辑甄选并整理成文。
                  我们以观点清晰、来源可靠为底线，只推荐自己也会读完的内容。
                </p>
                <Link
                  to="/about"
                  className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-accent-700 transition-colors hover:text-accent-900 whitespace-nowrap"
                >
                  认识看料编辑团队 <i className="ri-arrow-right-line" />
                </Link>
              </div>

              <Link
                to={`/category/${article.category}`}
                className="flex items-center justify-between rounded-xl bg-primary-500 p-5 text-background-50 transition-colors hover:bg-primary-600"
              >
                <div>
                  <p className="text-xs text-background-50/80">回到栏目</p>
                  <p className="mt-1 font-heading text-base font-bold">看更多「{article.categoryName}」好料</p>
                </div>
                <i className="ri-arrow-right-line text-xl" />
              </Link>
            </div>
          </aside>
        </div>
      </div>

      {/* 相关推荐 */}
      <section className="bg-background-100 border-t border-background-200/70">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="flex items-end justify-between gap-4">
            <h2 className="font-heading text-2xl font-black text-foreground-950">相关好料推荐</h2>
            <span className="text-sm text-foreground-500">你可能也会想读</span>
          </div>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}